import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import logAdCLSError from 'utils/log/logAdCLSError';
import { isMobileDevice } from 'utils/miscUtils/isMobileDevice';
import { trackOptimizelyEvent } from 'providers/OptimizelyProvider';
import { OPTIMIZELY_AD_ELEMENTS } from 'providers/OptimizelyProvider/utils';

const CLS_WATCHDOG_THRESHOLD_PX = parseInt(process.env.CLS_WATCHDOG_THRESHOLD_PX);
const CLS_WATCHDOG_MILLIS = parseInt(process.env.CLS_WATCHDOG_MILLIS);

const INTERACTION_EVENTS: Array<keyof GlobalEventHandlersEventMap> = [
  'click',
  'scroll',
  'resize',
  'keydown',
  'touchstart',
];

export interface AdLayoutWatchdogProps extends React.PropsWithChildren {
  matchAdUnits: string[];
}

type AdRenderEventMatch = {
  adUnitPath: string;
  creativeId: string;
  lineItemId: string;
};

export const AdLayoutWatchdog = ({ matchAdUnits }: AdLayoutWatchdogProps) => {
  const interactionListenersMounted = useRef(false);
  const detectedFirstUserInteraction = useRef(false);

  const capturedAdRenderEvent = useRef<AdRenderEventMatch | null>(null);
  // prevent duplicate impression events across instances
  // @ts-ignore
  if (!(globalThis as any).__ad_impression_fired_set) (globalThis as any).__ad_impression_fired_set = new Set<string>();
  // @ts-ignore
  const impressionFiredSet: Set<string> = (globalThis as any).__ad_impression_fired_set;

  // store observers and timers so we can disconnect/clear them on unmount
  const observersRef = useRef<
    Map<string, { io?: IntersectionObserver; mo?: MutationObserver; timerId?: number | null }>
  >(new Map());

  const clsTrackingInitialValue = useRef<number | null>(null);
  const clsTrackingRef = useRef<HTMLDivElement>(null);

  const [freestarLoaded, setFreestarLoaded] = useState(false);
  const [listenerEnqueued, setListenerEnqueued] = useState(false);
  const pubadsRef = useRef<any | null>(null);
  const slotHandlerRef = useRef<((event: any) => void) | null>(null);

  // Wait for Freestar to load
  // This might be redundant, as `window.freestar.queue` is technically available from the start, but it's best to be safe
  useEffect(() => {
    const intvl = setInterval(() => {
      if (window.freestar?.queue) {
        setFreestarLoaded(true);
        clearInterval(intvl);
      }
    }, 25);

    return () => clearInterval(intvl);
  }, []);

  // Mount Ad Rendered event listener
  useEffect(() => {
    if (!freestarLoaded) return;
    if (listenerEnqueued) return;

    window.freestar.queue.push(() => {
      const pubads = window.googletag.pubads();
      pubadsRef.current = pubads;

      const slotRenderHandler = (event: any) => {
        try {
          const adUnitPath = event.slot.getAdUnitPath();
          const creativeId = event.sourceAgnosticCreativeId;
          const lineItemId = event.sourceAgnosticLineItemId;

          // These are null if we can't display ads (i.e. consent rejected)
          if (creativeId === null && lineItemId === null) return;

          const matches = matchAdUnits.some((term) => adUnitPath.includes(term));

          // Store for CLS reporting
          if (matches) {
            capturedAdRenderEvent.current = {
              adUnitPath,
              creativeId: creativeId.toString(),
              lineItemId: lineItemId.toString(),
            };
          }

          const impressionKey = `${adUnitPath}::${creativeId}::${lineItemId}`;

          // if we've already fired for this creative/lineItem, skip
          if (impressionFiredSet.has(impressionKey)) return;

          const slotElementId =
            typeof event.slot.getSlotElementId === 'function' ? event.slot.getSlotElementId() : undefined;

          const attachObserver = (el: HTMLElement) => {
            if (!el || impressionFiredSet.has(impressionKey)) return;

            let isVisible = false;
            let timerId: number | null = null;

            const clearVisibilityTimer = () => {
              if (timerId !== null) {
                clearTimeout(timerId as number);
                timerId = null;
              }
            };

            const io = new IntersectionObserver(
              (entries) => {
                for (const entry of entries) {
                  const visibleNow = entry.isIntersecting && entry.intersectionRatio >= 0.5;
                  isVisible = visibleNow;

                  if (visibleNow) {
                    // start a 1s timer; require continuous visibility for 1s
                    if (timerId === null) {
                      timerId = window.setTimeout(() => {
                        if (isVisible && !impressionFiredSet.has(impressionKey)) {
                          impressionFiredSet.add(impressionKey);
                          try {
                            trackOptimizelyEvent(OPTIMIZELY_AD_ELEMENTS[slotElementId].name, {
                              value: OPTIMIZELY_AD_ELEMENTS[slotElementId].value,
                            });
                            trackOptimizelyEvent('ad_impressions', {
                              value: OPTIMIZELY_AD_ELEMENTS[slotElementId].value,
                            });
                          } catch {}

                          try {
                            io.disconnect();
                          } catch {}
                          const rec = observersRef.current.get(impressionKey);
                          if (rec?.mo) rec.mo.disconnect();
                          observersRef.current.delete(impressionKey);
                        }
                        timerId = null;
                      }, 1000) as unknown as number;
                      // store timerId so cleanup can access it
                      observersRef.current.set(impressionKey, { io, timerId });
                    }
                  } else {
                    // left visibility — cancel pending timer
                    clearVisibilityTimer();
                    const rec = observersRef.current.get(impressionKey);
                    if (rec) {
                      rec.timerId = null;
                      observersRef.current.set(impressionKey, rec);
                    }
                  }
                }
              },
              { threshold: [0, 0.5, 1] },
            );

            io.observe(el);
            // store observer record
            observersRef.current.set(impressionKey, { io, timerId: null });
          };

          if (slotElementId) {
            const el = document.getElementById(slotElementId);
            if (el) {
              attachObserver(el);
            } else {
              const mo = new MutationObserver(() => {
                const found = document.getElementById(slotElementId);
                if (found) {
                  attachObserver(found as HTMLElement);
                  mo.disconnect();
                }
              });
              mo.observe(document.body, { childList: true, subtree: true });
              observersRef.current.set(impressionKey, { mo, timerId: null });
            }
          }
        } catch {
          // swallow errors
        }
      };

      slotHandlerRef.current = slotRenderHandler;
      pubads.addEventListener('slotRenderEnded', slotRenderHandler);
    });

    setListenerEnqueued(true);
  }, [freestarLoaded, listenerEnqueued]);

  // cleanup pubads listener and any observers on unmount
  useEffect(() => {
    return () => {
      try {
        if (
          pubadsRef.current &&
          slotHandlerRef.current &&
          typeof pubadsRef.current.removeEventListener === 'function'
        ) {
          pubadsRef.current.removeEventListener('slotRenderEnded', slotHandlerRef.current);
        }
      } catch {}

      try {
        for (const [, rec] of observersRef.current) {
          if (rec.io) rec.io.disconnect();
          if (rec.mo) rec.mo.disconnect();
          if (rec.timerId) clearTimeout(rec.timerId as number);
        }
        observersRef.current.clear();
      } catch {}
    };
  }, []);

  // Mount CLS watchdog
  useEffect(() => {
    const intvl = setInterval(() => {
      // This should theoretically never happen (since the interval is cleared by the handler), but it's left here Just-In-Case (TM)
      // Break out of the function if the first interaction has already been detected
      if (detectedFirstUserInteraction.current) return;

      const top = clsTrackingRef.current?.getBoundingClientRect().top;

      if (top !== undefined) {
        if (clsTrackingInitialValue.current === null) {
          // Set the initial value
          clsTrackingInitialValue.current = top;
        } else {
          // Check for y-offset differences
          // If the difference is higher than the threshold, report it and unmount listeners
          const delta = Math.abs(top - clsTrackingInitialValue.current);
          if (delta > CLS_WATCHDOG_THRESHOLD_PX) {
            if (capturedAdRenderEvent.current !== null) {
              const { adUnitPath, creativeId, lineItemId } = capturedAdRenderEvent.current;
              logAdCLSError(
                window.location.pathname,
                isMobileDevice() ? 'mobile' : 'desktop',
                window.innerWidth,
                delta,
                adUnitPath,
                creativeId,
                lineItemId,
              );

              removeCLSWatchdog();
            }
          }
        }
      }
    }, CLS_WATCHDOG_MILLIS);

    // Cleanup function to stop the CLS Watchdog
    const removeCLSWatchdog = () => {
      if (!interactionListenersMounted.current) return;
      clearInterval(intvl);
      INTERACTION_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleFirstInteraction);
      });
      interactionListenersMounted.current = false;
    };

    // First interaction handler
    // Unmounts the CLS Watchdog; We don't want to track y-offsets if the user interacted
    const handleFirstInteraction = () => {
      if (detectedFirstUserInteraction.current) return;
      detectedFirstUserInteraction.current = true;
      console.log(`[ALW] First interaction detected`);
      removeCLSWatchdog();
    };

    INTERACTION_EVENTS.forEach((event) => {
      window.addEventListener(event, handleFirstInteraction);
    });
    interactionListenersMounted.current = true;

    return removeCLSWatchdog;
  }, []);

  return (
    <div
      style={{
        width: 0,
        height: 0,
        margin: 0,
        padding: 0,
        flexShrink: 0,
        alignSelf: 'flex-start',
        overflow: 'hidden',
        pointerEvents: 'none',
        visibility: 'hidden',
      }}
      aria-hidden
      data-cy={COMMON_TEST_IDS.AD_LAYOUT_WATCHDOG}
      ref={clsTrackingRef}
    />
  );
};
